import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const apiUrl = import.meta.env.VITE_API_URL;

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${apiUrl}/service/service-list/`);
        if (!response.ok) {
          throw new Error('Failed to fetch services');
        }
        const data = await response.json();
        setServices(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#040404] to-[#1a1a1a]">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="w-16 h-16 border-4 border-[#7bbf42] border-t-[#f9b414] rounded-full"
      ></motion.div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#040404] to-[#1a1a1a] text-red-500">
      Error: {error}
    </div>
  );

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-[#1a1a1a] to-[#040404] py-16 md:py-24 overflow-hidden">
      {/* Animated background elements */}
      <motion.div 
        initial={{ x: -100, y: -100 }}
        animate={{ x: 0, y: 0 }}
        transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
        className="absolute top-20 right-0 w-64 h-64 rounded-full bg-[#7bbf42]/10 blur-3xl"
      ></motion.div>
      
      <motion.div 
        initial={{ x: 100, y: 100 }}
        animate={{ x: 0, y: 0 }}
        transition={{ duration: 8, repeat: Infinity, repeatType: "reverse" }}
        className="absolute bottom-20 left-0 w-64 h-64 rounded-full bg-[#f9b414]/10 blur-3xl"
      ></motion.div>
      
      {/* Animated top border */}
      <motion.div 
        initial={{ width: 0 }}
        animate={{ width: "100%" }}
        transition={{ duration: 1 }}
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#7bbf42] via-[#f9b414] to-[#7bbf42]"
      ></motion.div>

      <div className="container pt-10 mx-auto px-4">
        {/* Section Header with animation */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span 
            whileHover={{ scale: 1.05 }}
            className="inline-block px-4 py-2 bg-[#7bbf42]/20 text-[#7bbf42] rounded-full mb-4 font-medium"
          >
            Our Services
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Building <motion.span 
              animate={{ color: ["#f9b414", "#7bbf42", "#f9b414"] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="text-[#f9b414]"
            >Excellence</motion.span> in Every Project
          </motion.h2>
          <motion.div 
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.4 }}
            className="w-20 h-1 bg-[#f9b414] mx-auto mb-6"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-gray-300 max-w-3xl mx-auto text-lg"
          >
            Comprehensive construction services tailored to meet your specific needs and exceed expectations
          </motion.p>
        </motion.div>

        {/* Services Grid with staggered animations */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div 
              key={index}
              variants={item}
              whileHover={{ y: -10 }}
              className="bg-[#040404]/80 backdrop-blur-sm border border-[#7bbf42]/20 rounded-xl overflow-hidden hover:border-[#f9b414]/40 transition-all duration-300 group"
            >
              {/* Service Image with parallax effect */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="relative overflow-hidden h-48"
              >
                <motion.img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040404]/90 to-transparent"></div>
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  className="absolute bottom-4 left-4"
                >
                  <span className="inline-block px-3 py-1 bg-[#f9b414] text-[#040404] text-sm font-bold rounded-full">
                    {service.category}
                  </span>
                </motion.div>
              </motion.div>
              
              {/* Service Content */}
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <motion.div 
                    whileHover={{ rotate: 15 }}
                    className="w-10 h-10 bg-[#7bbf42] rounded-full flex items-center justify-center mr-4"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.iconPath || "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"}></path>
                    </svg>
                  </motion.div>
                  <h3 className="text-xl font-bold text-white">{service.title}</h3>
                </div>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-gray-300 mb-6"
                >
                  {service.description}
                </motion.p>
                
                {/* Features List */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, i) => (
                    <motion.li 
                      key={i}
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * i }}
                      className="flex items-center"
                    >
                      <svg className="w-4 h-4 text-[#f9b414] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
                
           
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Ready to start your construction project?
          </h3>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-[#7bbf42] hover:bg-[#f9b414] text-[#040404] font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              Get a Free Quote
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceList;